import { useCallback, useEffect, useRef, useState } from 'react';
import { useSafeTimeout } from './useSafeTimeout';
import { useRetainedRef, type RetainedToken } from './retainedState';

// content-size 변화가 이 시간 동안 없으면 "안정"으로 보고 복원을 확정한다.
const STABLE_MS = 200;

export type ScrollRestoration = {
  /** 스크롤 정지 시점(onMomentumEnd/onEndDrag)에 현재 offset 저장. 워클릿에서 scheduleOnRN 으로 호출 가능. */
  save: (offset: number) => void;
  /** 리스트의 onContentSizeChange 에 연결. 콘텐츠가 자라 target 에 도달하면 1회 복원. */
  onContentSizeChange: (width: number, contentHeight: number) => void;
  /** 복원 위치가 잡힐 때까지 true — 리스트를 숨겨(opacity:0) "top 보였다 점프" 깜빡임 제거. */
  restoring: boolean;
};

/**
 * 동적 높이 리스트의 스크롤 위치를 visit(retainedState) 단위로 저장/복원한다.
 * 가려진 화면 unmount→remount(같은 visit)를 가로질러 위치를 보존한다.
 *
 * - 저장: save(offset) — 정지 시점에만(매 프레임 X)
 * - 복원: onContentSizeChange 로 콘텐츠가 target 까지 자라면 scrollTo 1회. 못 닿거나 로딩이
 *   늦으면 maxWaitMs 후 도달 최대치로 클램프하며 노출(무한 블랭크 방지).
 * - restoring 동안 리스트를 숨겨 깜빡임 제거. 새 visit(저장값 0)은 복원/숨김 하지 않음.
 *
 * token 은 호출부 모듈 스코프에 `retainedState(0)` 로 선언해 넘긴다.
 */
export const useScrollRestoration = (params: {
  token: RetainedToken<number>;
  /** offset 으로 스크롤(보통 listRef.scrollToOffset 래핑). 복원은 animated=false 로 호출된다. */
  scrollTo: (offset: number, animated: boolean) => void;
  /** 리스트 뷰포트 높이 — contentHeight 가 target+viewport 이상이어야 target 도달 가능. */
  viewportHeight: number;
  maxWaitMs?: number;
}): ScrollRestoration => {
  const { token, scrollTo, viewportHeight, maxWaitMs = 2000 } = params;
  const setSafeTimeout = useSafeTimeout();
  const savedOffset = useRetainedRef(token);

  const restoredRef = useRef(false);
  // mount 시점 스냅샷(per-mount): 이번 mount 가 복원할 목표. 이후 save 는 다음 visit 용.
  const targetRef = useRef(savedOffset.current);
  const [restoring, setRestoring] = useState(targetRef.current > 0);

  // 리렌더 0 — visit 동안 동일 retained 슬롯에 기록.
  // 단, 복원이 끝나기 전(restoring)엔 저장하지 않는다. 복원 중 콘텐츠 높이 요동으로
  // 스크롤이 클램프되며 튀는 값(예: 7378)이 저장돼 다음 복원 target 을 오염시키던 문제 방지.
  const save = useCallback(
    (offset: number) => {
      if (!restoredRef.current) return;
      savedOffset.current = offset;
    },
    [savedOffset],
  );

  const finish = useCallback(() => {
    restoredRef.current = true;
    setRestoring(false);
  }, []);

  // 확정 조건은 "target 도달" 이 아니라 **콘텐츠 성장 정지** 다.
  //
  // 도달 조건(contentHeight >= target + viewport)만 쓰면 target 이 도달 불가능할 때
  // (콘텐츠가 이전 방문보다 짧아진 경우) 영원히 만족되지 않아 backstop 을 다 기다린다.
  // 실측: target 17795 / needed 18670 인데 콘텐츠가 18522 ↔ 19379 로 진동해 reached 가
  // 깜빡이고 결국 backstop(2s) + JS 기아(1.9s) = 3.8s 동안 리스트가 가려졌다.
  //
  // 그래서 "지금까지 본 최대 높이" 를 기준으로, **더 자랄 때만** 확정을 미룬다.
  // 진동으로 낮아지는 이벤트는 무시하므로 디바운스가 리셋되지 않는다.
  const maxContentRef = useRef(0);
  const seqRef = useRef(0);
  const scheduleFinish = useCallback(() => {
    const mySeq = ++seqRef.current;
    setSafeTimeout(() => {
      if (restoredRef.current) return;
      if (mySeq !== seqRef.current) return; // 그 사이 더 자랐음 → 아직 성장 중
      scrollTo(targetRef.current, false);
      finish();
    }, STABLE_MS);
  }, [setSafeTimeout, scrollTo, finish]);

  const onContentSizeChange = useCallback(
    (_width: number, contentHeight: number) => {
      if (restoredRef.current) return;
      const target = targetRef.current;
      if (target <= 0) {
        finish();
        return;
      }

      if (contentHeight >= target + viewportHeight) {
        // 도달 가능 → target 으로 재적용(진동 추종).
        scrollTo(target, false);
      } else {
        // 아직 짧음: 지연 mount 섹션(useMountWhenNearViewport)은 뷰포트 근처에서만 깨어나므로,
        // 현재 바닥까지 밀어(clamp) 다음 섹션을 mount 시킨다 → 콘텐츠가 자라며 content-size 재발생.
        // (리스트는 restoring 동안 숨김이라 이 과정은 보이지 않는다)
        scrollTo(contentHeight, false);
      }

      // 성장했을 때만 확정을 미룬다. 진동 하강은 무시 → 디바운스 리셋 없음.
      if (contentHeight <= maxContentRef.current) return;
      maxContentRef.current = contentHeight;
      scheduleFinish();
    },
    [viewportHeight, scrollTo, finish, scheduleFinish],
  );

  // 콘텐츠가 예전보다 짧거나 로딩이 늦어 target 에 못 닿는 경우의 backstop.
  useEffect(() => {
    if (targetRef.current <= 0) return;
    setSafeTimeout(() => {
      if (restoredRef.current) return;
      scrollTo(targetRef.current, false);
      finish();
    }, maxWaitMs);
  }, [setSafeTimeout, scrollTo, finish, maxWaitMs]);

  return { save, onContentSizeChange, restoring };
};
